import * as React           from "react";

import {
    faBan,
    faBrush,
    faCaretDown,
    faCaretUp,
    faPencil,
    faShoppingCart,
    faSquareCheck,
    IconDefinition,
}                        from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {cx}              from "emotion";
import ReactTooltip      from "react-tooltip";

import "./orders-table.scss";

import {AnyAction}                    from "@Common/react/any-action";
import {cs}                           from "@Common/react/chain-services";
import {
    ComponentLink,
    makeChain,
    valueHelper as value,
}                                     from "@Common/react/cst2";
import {UseState}                     from "@Common/react/use-state";
import {Types}                        from "@Common/types";
import {Dropdown}                     from "@Common/ui-components/dropdown/dropdown";
import {formatDate1}                  from "@Common/ui-components/format-date";
import {DataTableD1}                  from "@Common/ui-components/heavy/common/design2/data-table/data-table-d1";
import {getPageSummary}               from "@Common/ui-components/heavy/common/design2/data-table/get-page-summary";
import {Pagination}                   from "@Common/ui-components/heavy/common/design2/data-table/pagination";
import {ConfirmDialog}                from "@Common/ui-components/heavy/common/design2/dialogs/confirm-dialog/confirm-dialog";
import {PageList}                     from "@Common/ui-components/heavy/common/design2/pagination-ui/page-list/page-list";
import {PageSize}                     from "@Common/ui-components/heavy/common/design2/pagination-ui/page-size/page-size";
import {MEDIA_QUERY_MD, ViewportTest} from "@Common/ui-components/viewport-test";
import {buildUrlQuery}                from "@Common/utils/build-url-query";
import {consumeContext}                                         from "@Xpresso/loaders/consume-context";
import {AGENT_LEVEL, BRANCH_LEVEL, COMPANY_LEVEL, OFFICE_LEVEL} from "@Xpresso/routes/common/logic/user-level";
import {DesktopActions}     from "@Xpresso/routes/account/orders/common/order-actions/desktop-actions";
import {PROGRAM_TYPE}          from "@Xpresso/routes/common/logic/program";
import {getAbsLink} from "../../../../common/get-abs-link";
import {OrderCard}  from "../common/order-card";
import {Responsive2} from "@Common/ui-components/responsive-2";

type Item = Types.Apis.Payloads.Next.Item.GetPending.Item;

export const PendingOrdersTable = ({error, filterApplied, loading, onItemDeleted, rows, sortState}:{
    error          : boolean,
    filterApplied ?: boolean,
    loading        : boolean,
    onItemDeleted  : (obj:{
        orderId: string,
        cartId: number,
    }) => void,
    rows           : Item[],
    sortState      : Types.Common.React.UseState.UseStateNext<{id: string, ascending: boolean}>,
}) => (
    makeChain<JSX.Element>()
    .then(...consumeContext("apis"))
    .then(...consumeContext("cart"))
    .then(...consumeContext("session"))
    .then(...consumeContext("userLevels"))
    .then(...consumeContext("company"))
    .then(...consumeContext("routerHistory"))
    .then(...consumeContext("systemConstants"))

    .then("mapSortIdAsColumnIndex", value(
        ({userLevels: {userLevel}}) => makeSortIdAsColumnIndexMap({userLevel})
    ))
    .then("mapColumnIndexAsSortId", value(
        ({userLevels: {userLevel}}) => makeColumnIndexAsSortIdMap({userLevel})
    ))

    .then("mapItemAsActions", value(
        ({apis, cart: {loading, value: sessionCart}, session, company, routerHistory, systemConstants}) => loading ? (() => []) : makeItemAsActionsMap({apis, session, sessionCart, company, routerHistory, systemConstants})
    ))

    .then("pageSizeState", ({}, next:ComponentLink<typeof UseState<number>>) => (
        UseState({initValue: 10, next})
    ))
    .then("pagination", ({pageSizeState: {value: pageSize}}, next:ComponentLink<typeof Pagination<Item>>) => (
        Pagination({next, pageSize, rows})
    ))
    .then(({mapColumnIndexAsSortId, mapItemAsActions, mapSortIdAsColumnIndex, pageSizeState, pagination, userLevels, routerHistory}) => (
        <div className="orders-table-9fi">
            {loading ? (
                <div className="loading" xd-qa-type="label" xd-qa-name="Loading">
                    Loading pending orders...
                </div>
            ) : rows?.length === 0 ? (
                <div className="no-pending-orders">
                    <p>{filterApplied ? "No results for your query" : "You don't have any pending order to complete!"}</p>
                    <button className="to-home" xd-qa-type="button" xd-qa-name="Back to the Home Page"
                        onClick={() => {
                            routerHistory.push(`/`);
                        }}
                    >
                        Back to the Home Page
                    </button>
                </div>
            ) : error ? (
                <div className="no-pending-orders">
                    <p>There was an error loading the orders.<br/>
                    Please go back and try again.<br/>
                    If the issue persists please contact us.</p>
                    <button
                        className="to-home"
                        xd-qa-type="button"
                        xd-qa-name="Back to the Home Page"
                        onClick={() => {
                            routerHistory.push(`/`);
                        }}
                    >
                        Back to the Home Page
                    </button>
                </div>
            ) : (
                <div className="table">
                    <ViewportTest if={MEDIA_QUERY_MD} then={() => (
                        makeChain<React.ReactNode>()
                        .then(...consumeContext("apis"))
                        .then("state", ({}, next:ComponentLink<typeof UseState<{expandedItem?: Item, dropdownPosition?: {right: number, top: number}}>>) => (
                            UseState({next, initValue: {}})
                        ))
                        .then(({apis, state: {onChange: setState, value: {dropdownPosition, expandedItem}}}) => <>
                            {expandedItem && (
                                <MobileActions
                                    actions={mapItemAsActions({item: expandedItem})}
                                    footer={
                                        expandedItem.programType === PROGRAM_TYPE.SCHEDULED_CAMPAIGNS
                                        && <ScheduledCampaignsTooltip/>
                                    }
                                    position={dropdownPosition}
                                    onClose={() => setState({})}
                                    onDelete={() => {
                                        if (window.confirm("Delete pending order?")) {
                                            deletePendingItem({apis, itemId: expandedItem.orderId, onDeleted: () => onItemDeleted({orderId: expandedItem.orderId, cartId: expandedItem?.idCart})});
                                        }
                                    }}
                                />
                            )}
                            {pagination.getDisplayedRows().map((item) => (
                                OrderCard({
                                    orderId   : item.orderId,
                                    orderName : item.orderName,
                                    details: [
                                        {label: "Program", value: item.programName},
                                        ...(userLevels.userLevel === COMPANY_LEVEL ? [{label: "Office",  value: item.branchName}] : []),
                                        ...(userLevels.userLevel !== AGENT_LEVEL   ? [{label: "User",    value: `${item.agentNameFirst} ${item.agentNameLast}`}] : []),
                                        {label: "Date", value: new Date(item.date.date).toLocaleDateString()},
                                        ...(item.idCart                            ? [{label: "Cart ID", value: item.idCart}] : []),
                                    ],
                                    onOptionsClick : ({currentTarget: {clientWidth, offsetLeft, offsetParent, offsetTop}}) => (
                                        // timeout to queue after MobileActions close
                                        window.setTimeout(() => setState({
                                            expandedItem     : item,
                                            dropdownPosition : {right: offsetParent.clientWidth - offsetLeft - clientWidth, top: offsetTop}
                                        }), 0)
                                    ),
                                })
                            ))}
                        </>)
                        .run()
                    )} else={() => cs(
                        ["responsive", (_, next) => Responsive2({
                            translate: (w) => w < 1280 ? "md" : w < 1400 ? "lg" : "xl",
                            next,
                        })],
                        ({responsive}) => (
                            DataTableD1({
                                customSorting : makeDataTableSorting({mapColumnIndexAsSortId, mapSortIdAsColumnIndex, sortState}),
                                rows          : pagination.getDisplayedRows().map((r) => ({
                                    xdQaName: r?.orderId,
                                    columns: [
                                        {
                                            render: () => !r.legacyOrder && r?.idCart && cs(
                                                consumeContext("session"),
                                                ({session}, next) => (
                                                    <a onClick = {() => checkoutOrder({order: r, session, routerHistory})}>
                                                    {next()}</a>
                                                ),
                                                () => (<>
                                                    <i className="fa fa-shopping-cart" xd-qa-type="icon" xd-qa-name="cart"/>
                                                    <span title="Cart ID" className="id">{r.idCart}</span>
                                                </>)
                                            ),
                                            sortValue: () => r.idCart,
                                            domAttrs: {
                                                "xd-qa-type": "icon",
                                                "xd-qa-name": "Cart",
                                            },
                                        },
                                        {
                                            render: () => (
                                                <div className="actions">
                                                    <DesktopActions
                                                        actions={mapItemAsActions({item: r})}
                                                        footer={
                                                            r.programType === PROGRAM_TYPE.SCHEDULED_CAMPAIGNS
                                                            && <ScheduledCampaignsTooltip/>
                                                        }
                                                    />
                                                </div>
                                            ),
                                            domAttrs: {
                                                "xd-qa-type": "link",
                                                "xd-qa-name": "Place/Edit",
                                            },
                                        },
                                        {
                                            render: () => <>
                                                <ReactTooltip  id="orderName" place="bottom" delayShow={100}/>
                                                {r.orderName}
                                            </>,
                                            domAttrs: {
                                                "xd-qa-type": "label",
                                                "xd-qa-name": "ORDER NAME",
                                                "data-for": "orderName",
                                                "data-tip": r.orderName,
                                            },
                                        },
                                        {
                                            render: () => r.orderId,
                                            sortValue: () => r.orderId,
                                            domAttrs: {
                                                "xd-qa-type": "label",
                                                "xd-qa-name": "ORDER ID",
                                            },
                                        },
                                        responsive === "xl"  && {
                                            render: () => <>
                                                <ReactTooltip  id="programName" place="bottom" delayShow={100}/>
                                                {(r.programName ? r.programName : r.idProgram ?? '').toUpperCase()}
                                            </>,
                                            domAttrs: {
                                                "xd-qa-type": "label",
                                                "xd-qa-name": "TYPE",
                                                "data-for": "programName",
                                                "data-tip": (r.programName ? r.programName : r.idProgram ?? '').toUpperCase(),
                                            },
                                        },
                                        ( responsive === "lg" || responsive === "xl" ) && {
                                            render: () => r.product,
                                            domAttrs: {
                                                "xd-qa-type": "label",
                                                "xd-qa-name": "PRODUCT",
                                            },
                                        },
                                        (userLevels?.userLevel < 4) && {
                                            render: () => <>
                                                <ReactTooltip  id="branchName" place="bottom" delayShow={100}/>
                                                {r.branchName}
                                            </>,
                                            domAttrs: {
                                                "xd-qa-type": "label",
                                                "xd-qa-name": "OFFICE",
                                                "data-for": "branchName",
                                                "data-tip": r.branchName,
                                            },
                                        },
                                        (userLevels?.userLevel < 5) && {
                                            render: () => <>
                                                <ReactTooltip  id="agentName" place="bottom" delayShow={100}/>
                                                {(r.agentNameFirst === null && r.agentNameLast === null) ? (
                                                    "Branch Order"
                                                ) : (
                                                    r.agentNameFirst + ' ' + r.agentNameLast
                                                )}
                                            </>,
                                            domAttrs: {
                                                "xd-qa-type": "label",
                                                "xd-qa-name": "USER",
                                                "data-for": "agentName",
                                                "data-tip": (r.agentNameFirst === null && r.agentNameLast === null) ? (
                                                    "Branch Order"
                                                ) : (
                                                    r.agentNameFirst + ' ' + r.agentNameLast
                                                ),
                                            },
                                        },
                                        {
                                            render: () => formatDate1(new Date(r.date.date)),
                                            sortValue: () => new Date(r.date.date).getTime(),
                                            domAttrs: {
                                                "xd-qa-type": "label",
                                                "xd-qa-name": "DATE",
                                            },
                                        },
                                        {
                                            render: () => (
                                                <a className="cart-row-j0d action-cell">
                                                    {DeletePendingItem({
                                                        itemId: r.orderId,
                                                        onDeleted: () => onItemDeleted({orderId: r.orderId, cartId: r?.idCart}),
                                                    })}
                                                </a>
                                            ),
                                            domAttrs: {
                                                "xd-qa-type": "icon",
                                                "xd-qa-name": "DELETE",
                                            },
                                        },
                                    ].filter(v => v),
                                })),
                                columns: columns({
                                    userLevel: userLevels?.userLevel,
                                    responsive,
                                }),
                                emptyAction: {
                                    label: "No orders found"
                                },
                            })
                        ),
                    )}/>

                    <div className="footer">
                        {rows.length > 10 ? (
                            PageSize({state: pageSizeState, xdQaNamePrefix: "Pending Orders", pageSizeMessage: ""})
                        ) : (
                            <div className="spacer"/>
                        )}

                        {pagination.active && PageList(pagination)}
                        {getPageSummary({
                            pageIndex: pagination.state.value,
                            pageSize: pageSizeState.value,
                            count: rows.length
                        })}
                    </div>
                </div>
            )}
        </div>
    ))
    .run()
);

interface ItemAction {
    icon: IconDefinition,
    label: string,
    name: string,
    run: () => Promise<void>,
}

const PRODUCT_TYPE_PICKPACK             = 6;
const PRODUCT_PROOFENGINE_CUSTCANVAS    = 0;
const PRODUCT_PROOFENGINE_XPRESSPROOF   = 7;
const ORDER_SOURCE_OIA                  = "OIA";

const DEFAULT_ACTION_TEMPLATE = {
    name  : "default",
    label : "Place/Edit",
    icon  : faPencil,
    run   : async () => {},
}

const ScheduledCampaignsTooltip = () => (
    <em>Changes will only apply to the current edition,
    and will not be applied to your Scheduled Campaign
    default preferences</em>
);

const makeItemAsActionsMap = ({apis, session, sessionCart, company, routerHistory, systemConstants}:{
    apis            : Types.Xpresso.Loaders.ContextNameMap['apis'],
    session         : Types.Xpresso.Loaders.ContextNameMap['session'],
    sessionCart     : Types.Xpresso.Loaders.ContextNameMap['cart']['value'],
    company         : Types.Xpresso.Loaders.ContextNameMap['company']
    routerHistory   : Types.Xpresso.Loaders.ContextNameMap['routerHistory']
    systemConstants : Types.Xpresso.Loaders.ContextNameMap['systemConstants']
}) => (
    ({item}:{item: Item}) => {
        const actions:Array<ItemAction> = [];
        if (!item.legacyOrder && item?.idCart) {
            actions.push({
                name  : "checkout",
                label : "Go to Cart",
                icon  : faShoppingCart,
                run   : () => checkoutOrder({session, order: item, routerHistory}),
            });
        }

        if (sessionCart?.status === "processing" && item.idCart === sessionCart.cartId) {
            actions.push({
                ...DEFAULT_ACTION_TEMPLATE,
                run: async () => window.alert("This cart is processing checkout. Please wait until this process is finished."),
            });
        } else if (item.programType === PROGRAM_TYPE.APM2) {
            actions.push({
                ...DEFAULT_ACTION_TEMPLATE,
                run: () => new Promise(async (resolve) => {
                    await apis.next.session.emulateAgent({branchId: item.idBranch, agentId: item.idAgent}); // lol
                    window.location = getAbsLink(`apm?event_id=${item.idEvent}`);

                    // delay link restore since should redirect sooner anyway
                    window.setTimeout(() => resolve(), 1000);
                })
            });
        } else if (item.legacyOrder || item.idProductType === PRODUCT_TYPE_PICKPACK) {
            actions.push({
                ...DEFAULT_ACTION_TEMPLATE,
                run: async () => routerHistory.push(`/product-options/order/${item.orderId}`),
            });
        } else {
            actions.push({
                name  : "product-options",
                label : "Product Options",
                icon  : faSquareCheck,
                run   : async () => routerHistory.push(`/product-options/order/${item.orderId}`),
            });

            const isCC     = item.proofEngineType === PRODUCT_PROOFENGINE_CUSTCANVAS;
            const isXProof = item.proofEngineType === PRODUCT_PROOFENGINE_XPRESSPROOF;
            if (company?.prefs?.xpressoKitOptionsActive && item.isKit) {
                actions.push({
                    name  : "kit-options",
                    label : "Kit Options",
                    icon  : faBrush,
                    run   : async () => routerHistory.push(`/kit-options/order/${item.orderId}`),
                });
            }
            else if (
                (!company?.prefs?.xpressoKitOptionsActive || !item.isKit)
                && item.source !== ORDER_SOURCE_OIA
                && (isCC || isXProof)
                && !item.uyoMPB // no customization for brochures that use multipage validation
            ) {
                actions.push({
                    name  : "customize",
                    label : "Customize",
                    icon  : faBrush,
                    run   : async () => {
                        const fullItem = await apis.next.item.getItem(item.orderId);
                        const templateOptions = await apis.next.getTemplateOptions(fullItem.sku);
                        const build3SupportedBuildPaths = JSON.parse(systemConstants?.value?.BUILD3_SUPPORTED_BUILDPATHS);

                        // if uses multipage validation, forward to product options - not preventing the option
                        // altogether because of the performance hit to look it up, and this case only applies to
                        // legacy orders using products that were converted to use multipage validation
                        if (templateOptions.enableMultipageValidation)
                            return routerHistory.push(`/product-options/order/${item.orderId}`);

                        const buildPath = templateOptions.build_path;
                        if (isCC || (company.prefs.allowBuild3 && (!buildPath || build3SupportedBuildPaths.includes(buildPath))) ) {
                            routerHistory.push(`/customize/order/${item.orderId}`);
                        } else {
                            window.location = mapOrderAsLegacyEditUrl(item, {'redirect': "customize"});
                        }
                    },
                });
            }
        }

        return actions;
    }
);

const MobileActions = ({actions, footer, onClose, onDelete, position}:{
    actions  : ItemAction[],
    footer  ?: React.ReactNode,
    onClose  : () => void,
    onDelete : () => void,
    position : {right: number, top: number},
}) => (
    <div className="mobile-dropdown" style={position}>
        <AnyAction action={onClose}/>
        {actions.map(action => (
            <a
                key={action.name}
                className="dropdown-item"
                xd-qa-type="dropdown_item"
                xd-qa-name={action.label}
                onClick={() => action.run()}
            >
                <i className="icon">{action.icon && <FontAwesomeIcon icon={action.icon}/>}</i>
                {action.label}
            </a>
        ))}
        <a
            key="delete"
            className="delete dropdown-item"
            xd-qa-type="dropdown_item"
            xd-qa-name="Delete"
            onClick={onDelete}
        >
            <i className="icon"><FontAwesomeIcon icon={faBan}/></i>
            Delete
        </a>
        {footer && (
            <div className="dropdown-footer">{footer}</div>
        )}
    </div>
);

const mapOrderAsLegacyEditUrl = (order:Item, params?:any) =>
{
    const query = {
        'action'       : "edit",
        'order_exists' : "exists",
        'is_api_order' : "legacy",
        'id_order'     : order.orderId,
        'order_type'   : order.product,
        'is_cc_order'  : order.proofEngineType === PRODUCT_PROOFENGINE_CUSTCANVAS ? "cc_order" : "xpressproof",
        'procform'     : 1,
        ...params,
    };
    return getAbsLink(`next/start.php${buildUrlQuery(query)}`);
};

const DeletePendingItem = ({itemId, onDeleted}:{itemId: string, onDeleted: () => void}) => cs(
    consumeContext("apis"),
    ["confirmModal", ({}, next) => ConfirmDialog({next})],
    ({confirmModal, apis}) => (

        <img
            className="trash"
            alt="Delete"
            src={require("../../../../../../public/assets/trash2.svg")}
            xd-qa-type="icon"
            xd-qa-name="delete"
            onClick={async () => {
                if (await confirmModal.show({
                    message: "Delete Pending Order",
                    buttons: {
                        confirmLabel: "Yes",
                    }
                })) {
                    deletePendingItem({apis, itemId, onDeleted});
                }
            }}
        />

    ),
);

export const checkoutOrder = async ({order, session, routerHistory}:{
    order         : Item,
    session       : Types.Xpresso.Loaders.ContextNameMap['session'],
    routerHistory : Types.Xpresso.Loaders.ContextNameMap['routerHistory'],
}) => {
    await session.applyEmulation({
        branchId: order.idBranch,
        ... (order.idAgent === "Branch Order") ? {
            isBranchOrder: true,
        } : {
            agentId: order.idAgent,
        },
    });

    if (order.programType === PROGRAM_TYPE.APM2) {
        window.location.href = `/apm/checkout?event_id=${order.idEvent}`;
    } else {
        routerHistory.push(`/checkout/${order.idCart}`)
    }
}

export const deletePendingItem = async ({apis, itemId, onDeleted}:{
    apis      : Types.Xpresso.Loaders.ContextNameMap['apis'],
    itemId    : string,
    onDeleted : () => void
}) => {
    const result = await apis.next.item.deleteItem(itemId);
    if (result.result === 'failure' || result.result !== 0) {
        alert(result.errors?.msg ?? result?.message);
        return;
    }
    onDeleted?.();
};

const columns = ({userLevel, responsive}) => [
    {
        label: "IN CART",
        className: "in-cart",
        width: "80px",
        sortable: {
            defaultDirection: "asc" as const,
        },
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "IN CART",
        },
    },
    {
        label: "EDIT ORDER",
        className: "edit-order",
        width: "100px",
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "EDIT ORDER",
        },

    },
    {
        label: "ORDER NAME",
        className: "order-name",
        width: "auto",
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "ORDER NAME",
        },
    },
    {
        label: "ORDER ID",
        className: "order-id",
        width: "100px",
        sortable: {
            defaultDirection: "asc" as const,
        },
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "ORDER ID",
        },
    },
    responsive === "xl" && {
        label: "TYPE",
        className: "type",
        width: "100px",
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "TYPE",
        },
    },
    ( responsive === "lg" || responsive === "xl" ) && {
        label: "PRODUCT",
        className: "type",
        width: "80px",
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "PRODUCT",
        },
    },
    (userLevel < OFFICE_LEVEL) && {
        label: "OFFICE",
        className: "office",
        width:  ( responsive === "lg" || responsive === "xl" ) ? "150px" : "80px",
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "OFFICE",
        },
        sortable: {
            defaultDirection: "asc" as const,
        },
    },
    (userLevel < AGENT_LEVEL) && {
        label: "USER",
        className: "user",
        width:  ( responsive === "lg" || responsive === "xl" ) ? "150px" : "80px",
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "USER",
        },
        sortable: {
            defaultDirection: "asc" as const,
        },
    },
    {
        label: "DATE",
        className: "date",
        width:  ( responsive === "lg" || responsive === "xl" ) ? "100px" : "80px",
        sortable: {
            defaultDirection: "desc" as const,
        },
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "DATE",
        },
    },
    {
        label: "DELETE",
        className: "delete",
        width:  ( responsive === "lg" || responsive === "xl" ) ? "80px" : "50px",
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "DELETE",
        },
    },
].filter(v => v);

// Reconcile view sort with DataTable sort
const makeDataTableSorting = ({
    mapColumnIndexAsSortId,
    mapSortIdAsColumnIndex,
    sortState: {
        change: changeSort,
        value: {ascending: sortAscending, id: sortId}
    },
}:{
    mapColumnIndexAsSortId : (index:number) => string,
    mapSortIdAsColumnIndex : (id:string)    => number,
    sortState              : Types.Common.React.UseState.UseStateNext<{id: string, ascending: boolean}>,
}): Parameters<typeof DataTableD1>[0]['customSorting'] => ({
    apply                    : (rows:any) => rows,
    direction                : sortAscending ? "asc" : "desc",
    isSortingActiveForColumn : ({columnIndex}) => columnIndex === mapSortIdAsColumnIndex(sortId),
    toggleColumn             : ({columnIndex, defaultDirection}) => changeSort(
        ({id, ascending}) => {
            const toggledSortId = mapColumnIndexAsSortId(columnIndex);
            return ({
                id        : toggledSortId,
                ascending : toggledSortId === id ? !ascending : defaultDirection === "asc",
            });
        }
    ),
    value                    : {
        columnIndex : mapSortIdAsColumnIndex(sortId),
        direction   : sortAscending ? "asc" : "desc",
    },
});

const makeColumnIndexAsSortIdMap = ({userLevel}:{userLevel: number}) => (
    (index:number): string => {
        switch (index) {
            case 6  : return (userLevel === COMPANY_LEVEL ? "branchId" : (userLevel === BRANCH_LEVEL ? "agentName" : "date"));

            case 7  : return (userLevel === COMPANY_LEVEL ? "agentName" : "date");

            case 8  : return "date";

            case 0  : return "cartId";

            case 3  :
            default : return "orderId";
        }
    }
);

const makeSortIdAsColumnIndexMap = ({userLevel}:{userLevel: number}) => (
    (sortId:string): number => {
        switch (sortId) {
            case "branchName" : return 6;

            case "agentName"  : return 6 + (userLevel === COMPANY_LEVEL ? 1 : 0);

            case "date"     : return 6 + (userLevel === COMPANY_LEVEL ? 2 : (userLevel === BRANCH_LEVEL ? 1 : 0));

            case "cartId"   : return 0;

            case "orderId"  :
            default         : return 3;
        }
    }
);

